import { trackEvent } from "analytics";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import { VerticallyExpandableContainer } from "designer/ui/smart/vertically-expandable-container/vertically-expandable-container";
import { useAppID } from "hooks/use-app-id";
import { GlideFC } from "lib/glide-fc";
import { useEffect, useMemo, useState } from "react";
import { styled } from "sharedUI/styles";
import { stripeClient } from "webapp/lib/stripe-api";

const StripePayButtonStyle = styled.div`
    padding: 18px 0;

    #stripe-pay-button {
        border-radius: 5px;
        overflow: hidden;
    }

    .divider {
        position: relative;
        margin: 30px 0 4px;

        hr {
            border: none;
            border-top: 1px solid ${p => p.theme.borderColor};
        }

        p {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: ${p => p.theme.bgColorLight};
            padding: 0 16px;
            font-size: 12px;
            color: ${p => p.theme.textPale};
        }
    }
`;

const SmallLoaderStyle = styled.div`
    position: relative;
    height: 60px;
    width: 100%;

    & .loader {
        transform: scale(0.4);
    }
`;

// Please React ship concurrent mode ASAP.
function useStripe() {
    const [stripe, setStripe] = useState<stripe.Stripe | undefined>(undefined);

    useEffect(() => {
        const fn = async () => {
            const stripeSDK = await stripeClient();
            setStripe(stripeSDK);
        };

        void fn();
    }, []);

    return stripe;
}

export interface PaymentRequestInfo {
    estimateDollars: number;
    label: string;
}

interface MaybeStripePayButtonProps {
    paymentRequestInfo: PaymentRequestInfo;
    onPay(methodId: string): Promise<boolean>;
}

export const MaybeStripePayButton: GlideFC<MaybeStripePayButtonProps> = p => {
    const { onPay, paymentRequestInfo } = p;

    // undefined means we don't know yet.
    const [canMakePayments, setCanMakePayments] = useState<boolean | undefined>(undefined);
    const stripe = useStripe();

    const paymentRequest = useMemo(() => {
        if (stripe === undefined) {
            return undefined;
        }

        return stripe.paymentRequest({
            country: "US",
            currency: "usd",
            total: {
                /**
                 * We don't care about amount and label right now
                 * we'll update with the real values before showing the dialog
                 */
                label: "Dummy label",
                amount: 0,
            },
            requestPayerName: true,
            requestPayerEmail: true,
        });
    }, [stripe]);

    useEffect(() => {
        if (paymentRequest === undefined) {
            return;
        }

        const checkPayments = async () => {
            const availablePayments = await paymentRequest.canMakePayment();

            if (availablePayments !== null) {
                setCanMakePayments(true);
            } else {
                setCanMakePayments(false);
            }
        };

        void checkPayments();
    });

    let content: React.ReactNode;

    if (stripe === undefined || paymentRequest === undefined || canMakePayments === undefined) {
        content = (
            <SmallLoaderStyle>
                <LoadingIndicator className="loader" />
            </SmallLoaderStyle>
        );
    } else if (!canMakePayments) {
        content = null;
    } else {
        content = (
            <StripePayButton
                onPay={onPay}
                paymentRequestInfo={paymentRequestInfo}
                stripe={stripe}
                paymentRequest={paymentRequest}
            />
        );
    }

    return <VerticallyExpandableContainer isOpen={true}>{content}</VerticallyExpandableContainer>;
};

interface StripePayButtonProps {
    paymentRequestInfo: PaymentRequestInfo;
    stripe: stripe.Stripe;
    paymentRequest: stripe.paymentRequest.StripePaymentRequest;
    onPay(methodId: string): Promise<boolean>;
}

const StripePayButton: GlideFC<StripePayButtonProps> = p => {
    const { paymentRequestInfo, stripe, paymentRequest, onPay } = p;

    const appID = useAppID();

    const button = useMemo(() => {
        const elements = stripe.elements();

        return elements.create("paymentRequestButton", {
            paymentRequest,
            style: {
                paymentRequestButton: {
                    theme: "dark",
                    height: "40px",
                },
            },
        });
    }, [paymentRequest, stripe]);

    useEffect(() => {
        button.mount("#stripe-pay-button");

        return () => {
            button.destroy();
        };
    }, [button]);

    useEffect(() => {
        button.on("click", () => {
            trackEvent("tried pay button", { label: paymentRequestInfo.label, app_id: appID ?? "unknown" });
            paymentRequest.update({
                currency: "usd",
                total: {
                    label: paymentRequestInfo.label,
                    amount: paymentRequestInfo.estimateDollars * 100,
                },
            });
        });
    }, [appID, button, paymentRequest, paymentRequestInfo.estimateDollars, paymentRequestInfo.label]);

    useEffect(() => {
        // Stupid stripe doesn't have a way of cleaning up events...
        let eventWasCancelled = false;

        paymentRequest.on("paymentmethod", async ev => {
            if (eventWasCancelled) {
                return;
            }

            const isSuccess = await onPay(ev.paymentMethod.id);
            if (isSuccess) {
                trackEvent("used pay button", { label: paymentRequestInfo.label, app_id: appID ?? "unknown" });
                ev.complete("success");
            } else {
                trackEvent("pay button failed", { label: paymentRequestInfo.label, app_id: appID ?? "unknown" });
                ev.complete("fail");
            }
        });

        return () => {
            eventWasCancelled = true;
        };
    }, [appID, onPay, paymentRequest, paymentRequestInfo.label]);

    return (
        <StripePayButtonStyle>
            <div id="stripe-pay-button" />
            <div className="divider">
                <hr />
                <p>or pay with card</p>
            </div>
        </StripePayButtonStyle>
    );
};
